import React, { useState, useEffect, useRef } from 'react';
import IceaDatePicker from './datepicker/IceaDatePicker';
import { calculateRC, calculateRA } from '../services/pensionApi';
import { DEFAULT_CALC, propString } from '../utils/calculatorProps';
import { formatCurrency } from '../utils/formatCurrency';
import { formatLocalYmd, parseLocalYmd } from '../utils/localDate';
import {
  calculateAgeFromDob,
  validateRetireWithTab,
  validateSaveMonthlyTab,
} from '../utils/pensionValidation';
import './PensionCalculator.css';

function isPensionApiCalcEnabled() {
  return typeof globalThis === 'undefined' || globalThis.__PENSION_CALC_API_ENABLED !== false;
}

const PensionCalculator = ({
  backURL,
  startURL,
  targetAmount: targetAmountProp,
  initialAmount: initialAmountProp,
  annualIncreaseRate: annualIncreaseRateProp,
  retirementAge: retirementAgeProp,
  monthlySavings: monthlySavingsProp,
}) => {
  const [activeTab, setActiveTab] = useState('retire-with');

  // Common fields
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [retirementAge, setRetirementAge] = useState(() =>
    propString(retirementAgeProp, DEFAULT_CALC.retirementAge)
  );

  // Tab 1: Amount to retire with
  const [targetAmount, setTargetAmount] = useState(() =>
    propString(targetAmountProp, DEFAULT_CALC.targetAmount)
  );

  // Tab 2: Amount to save per month
  const [initialAmount, setInitialAmount] = useState(() =>
    propString(initialAmountProp, DEFAULT_CALC.initialAmount)
  );
  const [annualIncreaseRate, setAnnualIncreaseRate] = useState(() =>
    propString(annualIncreaseRateProp, DEFAULT_CALC.annualIncreaseRate)
  );
  const [monthlySavings, setMonthlySavings] = useState(() =>
    propString(monthlySavingsProp, DEFAULT_CALC.monthlySavings)
  );

  // Calculation results
  const [savingDuration, setSavingDuration] = useState(0);
  const [monthlyContribution, setMonthlyContribution] = useState(0);
  const [totalMonthlyContribution, setTotalMonthlyContribution] = useState(0);
  const [initialContribution, setInitialContribution] = useState(0);
  const [totalContributions, setTotalContributions] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [maturityValue, setMaturityValue] = useState(0);
  const [interestRate, setInterestRate] = useState(0.0);

  // API interaction state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showValidation, setShowValidation] = useState(false);
  const lastSignatureRef = useRef('');
  const INTEREST_RATE = 10.0; // hardcoded as requested
  const minDobDate = useRef(new Date(new Date().getFullYear() - 100, 0, 1)).current;

  // Calculate savings duration
  useEffect(() => {
    if (!dateOfBirth) {
      // When DOB is empty, show 0 initially
      setSavingDuration(0);
      return;
    }
    const currentAge = calculateAgeFromDob(dateOfBirth);
    const retirementAgeNum = parseInt(retirementAge, 10) || 19;
    const duration = Math.max(0, retirementAgeNum - currentAge);
    setSavingDuration(duration);
  }, [dateOfBirth, retirementAge]);

  // Perform calculations (single effect for both tabs, immediate on tab change, debounced otherwise)
  useEffect(() => {
    if (!isPensionApiCalcEnabled()) return;

    setError('');

    const retirementAgeNum = Number(retirementAge);

    const { errors: tab1Errors, fieldErrors: tab1FieldErrors } = validateRetireWithTab({
      dateOfBirth,
      retirementAge: retirementAgeNum,
      targetAmount
    });

    const { errors: tab2Errors, fieldErrors: tab2FieldErrors } = validateSaveMonthlyTab({
      dateOfBirth,
      retirementAge: retirementAgeNum,
      monthlySavings,
      initialAmount,
      annualIncreaseRate
    });

    const tab1Ready = activeTab === 'retire-with' && tab1Errors.length === 0 && savingDuration > 0;
    const tab2Ready = activeTab === 'save-monthly' && tab2Errors.length === 0 && savingDuration > 0;

    if (!(tab1Ready || tab2Ready)) {
      setFormErrors(activeTab === 'retire-with' ? tab1Errors : tab2Errors);
      setFieldErrors(activeTab === 'retire-with' ? tab1FieldErrors : tab2FieldErrors);
      return;
    }

    setFormErrors([]);
    setFieldErrors({});

    const signature = JSON.stringify({
      activeTab,
      dateOfBirth,
      retirementAge: retirementAgeNum,
      targetAmount,
      initialAmount,
      annualIncreaseRate,
      monthlySavings,
    });

    if (lastSignatureRef.current === signature) return;

    const controller = new AbortController();

    const delay = 0;

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        lastSignatureRef.current = signature;

        if (activeTab === 'retire-with') {
          const payload = {
            TargetRetirementAmount: targetAmount,
            dob: dateOfBirth,
            RetirementAge: retirementAgeNum,
          };

          const r = await calculateRC(payload, { signal: controller.signal });
          const success = r.Success ?? r.success ?? true;
          if (!success) {
            setError(r.Message || r.message || 'Calculation failed');
            return;
          }

          const monthly = r.monthlyContribution ?? r.MonthlyContribution ?? r.requiredMonthly ?? 0;
          const totalContrib = r.totalContributions ?? r.TotalContributions ?? 0;
          const interest = r.totalInterest ?? r.TotalInterest ?? 0;
          const maturityFromPayload = r.maturityValue ?? r.MaturityValue ?? r.targetAmount;
          const savingYears = r.SavingDurationYears ?? r.savingDurationYears;

          setMonthlyContribution(r.MonthlyContribution ?? monthly);
          setTotalContributions(r.TotalContributions ?? totalContrib);
          setInterestRate(r.InterestRate ?? r.interestRate ?? INTEREST_RATE);
          setInitialContribution(r.InitialContribution ?? 0);
          setTotalInterest(r.TotalInterest ?? interest);
          setMaturityValue(
            r.TargetRetirementAmount ?? r.MaturityValue ?? maturityFromPayload ?? Number(targetAmount)
          );
          if (success && (r.SavingDurationYears !== undefined && r.SavingDurationYears !== null)) {
            setSavingDuration(savingYears);
          }
        } else {
          const payload = {
            MonthlyContribution: monthlySavings,
            dob: dateOfBirth,
            RetirementAge: retirementAgeNum,
            InitialAmount: Number(initialAmount || 0),
            AnnualIncreaseRate: Number(annualIncreaseRate || 0),
          };

          const r = await calculateRA(payload, { signal: controller.signal });
          const success = r.Success ?? r.success ?? true;
          if (!success) {
            setError(r.Message || r.message || 'Calculation failed');
            return;
          }

          const monthly = r.monthlyContribution ?? r.MonthlyContribution ?? Number(monthlySavings);
          const totalMonthly =
            r.TotalMonthlyContributions ?? r.totalMonthlyContributions ??
            r.totalMonthlyContribution ?? r.TotalMonthlyContribution ??
            (Number(monthly) * (savingDuration * 12));

          const initial = r.initialAmount ?? r.InitialAmount ?? Number(initialAmount || 0);
          const totalContrib = r.totalContributions ?? r.TotalContributions ?? (Number(initial) + Number(totalMonthly));
          const maturity = r.FinalRetirementAmount ?? r.finalRetirementAmount ?? r.maturityValue ?? r.MaturityValue ?? 0;
          const interest = r.totalInterest ?? r.TotalInterest ?? (Number(maturity) - Number(totalContrib));
          const savingYears = r.SavingDurationYears ?? r.savingDurationYears;

          setMonthlyContribution(monthly);
          setTotalMonthlyContribution(r.TotalMonthlyContributions ?? totalMonthly);
          setInitialContribution(initial);
          setTotalContributions(r.TotalContributions ?? totalContrib);
          setTotalInterest(r.TotalInterest ?? interest);
          setMaturityValue(r.FinalRetirementAmount ?? maturity);
          setInterestRate(r.InterestRate ?? r.interestRate ?? INTEREST_RATE);
          if (success && (r.SavingDurationYears !== undefined && r.SavingDurationYears !== null)) {
            setSavingDuration(savingYears);
          }
        }
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Calculation failed');
      } finally {
        setLoading(false);
      }
    }, delay);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [
    activeTab,
    dateOfBirth,
    retirementAge,
    targetAmount,
    initialAmount,
    annualIncreaseRate,
    monthlySavings,
    savingDuration,
  ]);

  return (
    <div className="pension-calculator">      
      <div className="calculator-header">
        <h1 className="regular text-primary-Blue-Tint-100 calculator-title">Calculate your retirement benefits</h1>
        <p className="calculator-description">
          Enter your proposed monthly contribution in terms of value or a percentage of your monthly income.
          You can also choose to input an initial amount which is a lump sum one-off payment.
        </p>
      </div>
      <div className="tab-buttons">
        <button
          className={`btn primary-button regular w-100 tab-button  ${activeTab === 'retire-with' ? 'active' : ''}`}
          onClick={() => setActiveTab('retire-with')}
        >
          Amount to retire with
        </button>
        <button
          className={`btn orangeSecondButton regular w-100 tab-button  ${activeTab === 'save-monthly' ? 'active' : ''}`}
          onClick={() => setActiveTab('save-monthly')}
        >
          Amount to save per month
        </button>
      </div>
      <div className="calculator-content">
        <div className="calculator-layout">
          <div className="form-section">
            <div className="form-group">
              <div className="top-box-calculator">
                <div className='date-box w-100'>
                    <label className="form-label required">Enter your Date of birth</label>
                    <div className="custom-datepicker-wrapper">
                      <IceaDatePicker
                        selected={parseLocalYmd(dateOfBirth)}
                        onChange={(date) => {
                          setDateOfBirth(formatLocalYmd(date));
                          setShowValidation(true);
                        }}
                        onBlur={() => setShowValidation(true)}
                        placeholderText="Select Date"
                        maxDate={new Date()}
                        minDate={minDobDate}
                        dateFormat="yyyy-MM-dd"
                        onChangeRaw={(e) => {
                          const value = e.target.value;
                          const enteredDate = new Date(`${value}T00:00:00`);
                          if (value && (!/^\d{4}-\d{2}-\d{2}$/.test(value) || enteredDate > new Date())) {
                            e.preventDefault();
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                    <p className="form-hint mb-0">Fill in your date of birth first before proceeding to the next step</p>
                    {showValidation && fieldErrors.dateOfBirth && <div className="error">{fieldErrors.dateOfBirth}</div>}
                </div>
                <div className='ammount-box w-100'>
                   {activeTab === 'retire-with' ? (
                    <div className="form-group">
                      <label className="form-label required">Target Retirement Amount</label>
                      <input
                        type="number"
                        className="form-input"
                        value={targetAmount}
                        onChange={(e) => { 
                          const value = e.target.value;
                          // Allow only positive numbers (including decimals up to 2 places)
                          if (/^\d*\.?\d{0,2}$/.test(value)) {
                            setTargetAmount(value);
                          }
                          setShowValidation(true); }}
                        onBlur={() => setShowValidation(true)}
                        placeholder="13,200"
                      />
                      <p className="form-hint mb-0">The retirement target amount you want to end with in your account</p>
                      {showValidation && fieldErrors.targetAmount && <div className="error">{fieldErrors.targetAmount}</div>}
                    </div>
                    ) : (
             
                      <div className="form-group">
                        <label className="form-label required">Initial Amount</label>
                        <input
                          type="number"
                          className="form-input"
                          value={initialAmount}
                          onChange={(e) => { 
                            const value = e.target.value;
                            // Allow only positive numbers (including decimals up to 2 places)
                            if (/^\d*\.?\d{0,2}$/.test(value)) {
                              setInitialAmount(value);
                            } 
                            setShowValidation(true); }
                          }
                          onBlur={() => setShowValidation(true)}
                          placeholder="13,200"
                        />
                        <p className="form-hint mb-0">The initial amount you want to start with in your account</p>
                        {showValidation && fieldErrors.initialAmount && <div className="error">{fieldErrors.initialAmount}</div>}
                      </div>
                    )
                  }
                </div>
              </div>
              <div className='border-box-cal bg-secondaryBackgroundColor'></div>
              <h4 className='medium text-primary-Blue-Tint-100 mb-4'>Calculate Retirement Amount</h4>
              <div className='calculator-main-box'>
                  <div className='values-box'>
                      {activeTab === 'retire-with' ? (
                        <div className="form-group">
                          <label className="form-label">Retirement age</label>
                          <input
                            type="number"
                            className="form-input"
                            value={retirementAge}
                            onChange={(e) => { setRetirementAge(e.target.value); setShowValidation(true); }}
                            onBlur={() => setShowValidation(true)}
                            placeholder="19"
                          />
                          <p className="form-hint mb-0" >Fill in your desired retirement age. Minimum age is 19 Years</p>
                          {showValidation && fieldErrors.retirementAge && <div className="error">{fieldErrors.retirementAge}</div>}
                        </div>
                      ):(
                        <div className='calc-box'>
                          <div className="retire-box">
                            <label className="form-label">Retirement age</label>
                            <input
                              type="number"
                              className="form-input"
                              value={retirementAge}
                              onChange={(e) => { setRetirementAge(e.target.value); setShowValidation(true); }}
                              onBlur={() => setShowValidation(true)}
                              placeholder="19"
                            />
                            <p className="form-hint mb-0">Fill in your desired retirement age. Minimum age is 19 Years</p>
                            {showValidation && fieldErrors.retirementAge && <div className="error">{fieldErrors.retirementAge}</div>}
                          </div>
                          <div className="annual-box">
                            <label className="form-label">Annual Increase Rate</label>
                            <input
                              type="number"
                              className="form-input"
                              value={annualIncreaseRate}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Allow only positive numbers (including decimals up to 2 places)
                                if (/^\d*\.?\d{0,2}$/.test(value)) {
                                  setAnnualIncreaseRate(value);
                                }
                                setShowValidation(true); }}
                              onBlur={() => setShowValidation(true)}
                              placeholder="0"
                            />
                            <p className="form-hint mb-0">The annual increase rate must be above a positive number.</p>
                            {showValidation && fieldErrors.annualIncreaseRate && <div className="error">{fieldErrors.annualIncreaseRate}</div>}
                          </div>
                           <div className="monthly-amount-box">
                            <label className="form-label">Amount to save monthly</label>
                            <input
                              type="number"
                              className="form-input"
                              value={monthlySavings}
                              onChange={(e) => {
                                if (/^\d*\.?\d{0,2}$/.test(e.target.value)) {
                                  setMonthlySavings(e.target.value); 
                                }
                                setShowValidation(true); }}
                              onBlur={() => setShowValidation(true)}
                              placeholder="1000"
                            />
                            <p className="form-hint mb-0">Fill in your desired amount you want to save monthly. Minimum amount is 1000 KES</p>
                            {showValidation && fieldErrors.monthlySavings && <div className="error">{fieldErrors.monthlySavings}</div>}
                          </div>
                        </div>
                      )
                    }
                  </div>
                  <div className="result-box">
                    {loading && (
                      <div
                        className="result-box-loading-overlay"
                        aria-busy="true"
                        aria-live="polite"
                        data-testid="pension-api-loading-overlay"
                      >
                        <div className="result-box-loading-spinner" role="presentation" />
                        <span>Calculating…</span>
                      </div>
                    )}
                      <div className="summary-section">
                        <p className="regular text-primary-Blue-Tint-100 summary-title">Retirement Investment Summary</p>
                        <div className="summary-item">
                          <span className="summary-label">Saving Duration</span>
                          <span className="summary-value">{savingDuration} year</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Monthly Contribution</span>
                          <span className="summary-value">KES {formatCurrency(monthlyContribution)}</span>
                        </div>
                      {activeTab === 'save-monthly' && (
                        <>
                          <div className="summary-item">
                            <span className="summary-label">Total Monthly Contribution</span>
                            <span className="summary-value">KES {formatCurrency(totalMonthlyContribution)}</span>
                          </div>

                          <div className="summary-item">
                            <span className="summary-label">Initial Contribution</span>
                            <span className="summary-value">KES {formatCurrency(initialContribution)}</span>
                          </div>
                        </>
                      )}
                      <div className="summary-item">
                        <span className="summary-label">Total Contributions</span>
                        <span className="summary-value">KES {formatCurrency(totalContributions)}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Total Interest (calculated at {interestRate}% p.a)</span>
                        <span className="summary-value">KES {formatCurrency(totalInterest)}</span>
                      </div>
                      <div className="summary-item highlight">
                        <span className="summary-label">Maturity Value</span>
                        <span className="summary-value">KES {formatCurrency(maturityValue)}</span>
                      </div>
                    </div>
                  </div>
              </div>
            </div>
          </div>
        </div>
        <div className='border-box-cal bg-secondaryBackgroundColor'></div>
        <div className="calculator-actions">
          <button className="back-button" onClick={() => {
            if (backURL) {
              window.location.href = backURL;
            } else {
              window.history.back(); // fallback if no backURL provided
            }
          }}>Back</button>
          <button className="btn primary-button regular withicon-center" disabled={loading} onClick={() => {
            if (startURL) {
              const sessionData = {
                journeyType: "nb",
                selectedVariation: "default"
              }
              sessionStorage.setItem("journeyData", JSON.stringify(sessionData));
              sessionStorage.setItem("leadId", 0);
              sessionStorage.setItem("journeyId", 0);
              window.location.href = startURL;
            }
          }}>{loading ? 'Calculating…' : 'Start Here'} </button>
        </div>
      </div>
    </div>
  );
};

export default PensionCalculator;
